import { getPathLengthAtPoint } from '../utils/workflow-map-state'

export default {
  data() {
    return {
      marker: { all: [0, 0, 0], employee: [0, 0, 0], contractor: [0, 0, 0] },
      visible: { all: false, employee: false, contractor: false },
      trackLengths: [],
      trackStops: [],
      trackRanges: [],
      trackReady: false
    }
  },
  watch: {
    progress: 'renderRoute',
    activeStep: 'renderRoute',
    workflow: 'resetMap'
  },
  mounted() {
    this.$nextTick(this.measureTracks)
    window.addEventListener('resize', this.followMarker)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.followMarker)
  },
  methods: {
    onWheel(event) {
      if (event.ctrlKey) return
      event.preventDefault()
      this.$emit('scroll-story', event)
    },
    legendRole(label) {
      return this.workflow.roles.find(item => item.label === label)?.id || 'all'
    },
    trackRole(track) {
      if (track.className === 'track-emp') return 'employee'
      if (track.className === 'track-con') return 'contractor'
      return 'all'
    },
    translate(point) {
      return `translate(${point[0]},${point[1]})`
    },
    lengthAtProgress(index) {
      const range = this.trackRanges[index]
      const stops = this.trackStops[index]
      if (!range || !stops) return 0
      const progress = Math.max(range.start, Math.min(range.end, this.progress))
      const lower = Math.floor(progress)
      const upper = Math.min(range.end, lower + 1)
      return stops[lower] + (stops[upper] - stops[lower]) * (progress - lower)
    },
    trackStyle(index, base = false) {
      const length = this.trackLengths[index] || 0
      const range = this.trackRanges[index]
      const stops = this.trackStops[index]
      if (!length || !range || !stops) return { opacity: 0 }
      const start = stops[range.start]
      const end = base ? stops[range.end] : this.lengthAtProgress(index)
      return {
        // A leading gap trims the path without altering transcript geometry.
        strokeDasharray: `0 ${start} ${Math.max(0, end - start)} ${length + 1}`,
        opacity: this.trackReady ? (base ? 0.58 : 0.92) : 0
      }
    },
    measureTracks() {
      if (!this.$el) return
      this.trackPaths = Array.from(this.$el.querySelectorAll('.wv-track-progress'))
      const routes = this.workflow.map.routes
      const lastStep = Number(this.workflow.steps[this.workflow.steps.length - 1].number)
      const ranges = this.workflow.map.trackRanges.map((range, index) => {
        let { start, end } = range
        const role = this.trackRole(this.workflow.map.tracks[index])
        // Branch paths include their join curve up to the actual shared station.
        if (role !== 'all') {
          while (end < lastStep && routes[end]?.mode === 'split') end++
        } else {
          while (start < end && routes[start]?.mode === 'split') start++
        }
        return { start, end }
      })
      this.trackRanges = ranges
      this.trackLengths = this.trackPaths.map(path => path.getTotalLength())
      this.trackStops = this.trackPaths.map((path, index) => {
        const range = ranges[index]
        const role = this.trackRole(this.workflow.map.tracks[index])
        const stops = {}
        for (let step = range.start; step <= range.end; step++) {
          const route = routes[step]
          const point = route.mode === 'split' ? route[role] : route.all
          stops[step] = getPathLengthAtPoint(path, point)
        }
        return stops
      })
      this.trackReady = true
      this.renderRoute()
    },
    resetMap() {
      this.trackReady = false
      this.visible = { all: false, employee: false, contractor: false }
      this.$nextTick(this.measureTracks)
    },
    pointOnTrack(index, waiting) {
      const point = this.trackPaths[index].getPointAtLength(this.lengthAtProgress(index))
      return [point.x, point.y, waiting]
    },
    followMarker() {
      const viewport = this.$refs.mapViewport
      const svg = this.$refs.mapSvg
      if (!viewport?.clientHeight || viewport.scrollHeight <= viewport.clientHeight) return
      const matrix = svg.getScreenCTM()
      if (!matrix) return
      const point = svg.createSVGPoint()
      const marker = this.visible.all ? this.marker.all : [
        (this.marker.employee[0] + this.marker.contractor[0]) / 2,
        (this.marker.employee[1] + this.marker.contractor[1]) / 2
      ]
      point.x = marker[0]
      point.y = marker[1]
      const screenPoint = point.matrixTransform(matrix)
      viewport.scrollTop += screenPoint.y - viewport.getBoundingClientRect().top - viewport.clientHeight / 2
    },
    renderRoute() {
      if (!this.trackReady) return
      const tracks = this.workflow.map.tracks
      const route = this.workflow.map.routes[this.activeStep]
      const branch = tracks.findIndex((track, index) => {
        const range = this.trackRanges[index]
        return this.trackRole(track) !== 'all' && this.progress > range.start && this.progress < range.end
      })
      if (branch !== -1) {
        const range = this.trackRanges[branch]
        tracks.forEach((track, index) => {
          const role = this.trackRole(track)
          if (role !== 'all' && this.trackRanges[index].start === range.start) {
            this.marker[role] = this.pointOnTrack(index, route[role]?.[2] || 0)
          }
        })
        this.visible = { all: false, employee: true, contractor: true }
      } else {
        let shared = 0
        tracks.forEach((track, index) => {
          if (this.trackRole(track) === 'all' && this.progress >= this.trackRanges[index].start) shared = index
        })
        this.marker.all = this.pointOnTrack(shared, route.all?.[2] || 0)
        this.visible = { all: true, employee: false, contractor: false }
      }
      this.$nextTick(this.followMarker)
    }
  }
}
